import React, { useContext } from 'react';
import {
    Box,
    Drawer,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    Slider,
    Select,
    MenuItem,
    FormControl,
    Divider,
    IconButton,
    Autocomplete,
    TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { AppThemeContext } from 'App';
import { ColorTheme, colorThemes } from 'styles/colorThemes';
import { useTranslation } from 'react-i18next';

const FONT_OPTIONS = [
    { value: 'Roboto, Helvetica, Arial, sans-serif', label: 'Roboto' },
    { value: 'Inter, Helvetica, Arial, sans-serif', label: 'Inter' },
    { value: 'Montserrat, Helvetica, Arial, sans-serif', label: 'Montserrat' },
    { value: "'Open Sans', Helvetica, Arial, sans-serif", label: 'Open Sans' },
    { value: 'Poppins, Helvetica, Arial, sans-serif', label: 'Poppins' },
];

interface AppearanceDrawerProps {
    open: boolean;
    onClose: () => void;
}

const AppearanceDrawer: React.FC<AppearanceDrawerProps> = ({ open, onClose }) => {
    const {
        themeSelection,
        setThemeSelection,
        colorTheme,
        setColorTheme,
        density,
        setDensity,
        cornerRadius,
        setCornerRadius,
        fontFamily,
        setFontFamily,
    } = useContext(AppThemeContext);

    const { i18n } = useTranslation();

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 2,
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backdropFilter: 'blur(1px)',
                        backgroundColor: 'rgba(0, 0, 0, 0.28)',
                    },
                },

                paper: {
                    sx: {
                        zIndex: (theme) => theme.zIndex.drawer + 2,
                        width: { xs: '100%', sm: 380 },
                        maxWidth: '100vw',
                    },
                },
            }}
        >
            <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Appearance
                    </Typography>
                    <IconButton onClick={onClose} aria-label="close appearance settings">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.4 }}>
                    Personaliza como se ve el workspace en esta pagina
                </Typography>

                <Box sx={{ maskType: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Language
                    </Typography>
                    <Autocomplete
                        value={{ label: i18n.language === 'en' ? 'English' : i18n.language === 'es' ? 'Spanish' : i18n.language, value: i18n.language }}
                        options={[
                            { code: 'US', label: 'English', value: 'en' },
                            { code: 'ES', label: 'Spanish', value: 'es' },
                            /* { label: 'French', value: 'fr' },
                            { label: 'German', value: 'de' },
                            { label: 'Chinese', value: 'zh' }, */
                        ]}

                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => (
                            <TextField {...params} size="small" placeholder="Select language" fullWidth />
                        )}
                        onChange={(e, value) => {
                            if (value) {
                                i18n.changeLanguage(value.value);
                            }
                        }}
                        renderOption={(props, option) => {
                            const { key, ...optionProps } = props;
                            return (
                            <Box
                                key={key}
                                component="li"
                                sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                                {...optionProps}
                            >
                                <img
                                    loading="lazy"
                                    width="20"
                                    srcSet={`https://flagcdn.com/w40/${option.code?.toLowerCase()}.png 2x`}
                                    src={`https://flagcdn.com/w20/${option.code?.toLowerCase()}.png`}
                                    alt={`${option?.label} flag`}
                                />
                                {option.label}
                            </Box>
                            );
                        }}
                    />

                    <Typography variant="subtitle2" sx={{ my: 1, fontWeight: 600 }}>
                        Theme
                    </Typography>
                    <ToggleButtonGroup
                        value={themeSelection}
                        exclusive
                        onChange={(_, v) => v && setThemeSelection(v as 'system' | 'light' | 'dark')}
                        fullWidth
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                textTransform: 'capitalize',
                                flex: 1,
                                borderRadius: '6px !important',
                                border: '1px solid',
                                borderColor: 'divider',
                                '&.Mui-selected': {
                                    fontWeight: 600,
                                },
                            },
                        }}
                    >
                        <ToggleButton value="system">System</ToggleButton>
                        <ToggleButton value="light">Light</ToggleButton>
                        <ToggleButton value="dark">Dark</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {Object.entries(colorThemes).map(([key, { primary }]) => (
                            <Box
                                key={key}
                                onClick={() => setColorTheme(key as ColorTheme)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    gap: 0.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: '50%',
                                        bgcolor: primary[500],
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        outline: colorTheme === key
                                            ? `3px solid ${primary[500]}`
                                            : '3px solid transparent',
                                        outlineOffset: 2,
                                        transition: 'outline 0.15s, transform 0.15s',
                                        '&:hover': { transform: 'scale(1.1)' },
                                    }}
                                >
                                    {colorTheme === key && (
                                        <CheckIcon sx={{ color: '#fff', fontSize: 20 }} />
                                    )}
                                </Box>
                                <Typography
                                    variant="caption"
                                    sx={{ fontSize: '0.65rem', textTransform: 'capitalize', opacity: 0.8 }}
                                >
                                    {key}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Density
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {density}px
                        </Typography>
                    </Box>
                    <Slider
                        value={density}
                        min={4}
                        max={12}
                        step={1}
                        onChange={(_, v) => setDensity(v as number)}
                        marks={[
                            { value: 4, label: '4' },
                            { value: 8, label: '8' },
                            { value: 12, label: '12' },
                        ]}
                        valueLabelDisplay="auto"
                        size="small"
                    />
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Corner Radius
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {cornerRadius}px
                        </Typography>
                    </Box>
                    <Slider
                        value={cornerRadius}
                        min={0}
                        max={20}
                        step={1}
                        onChange={(_, v) => setCornerRadius(v as number)}
                        marks={[
                            { value: 0, label: '0' },
                            { value: 8, label: '8' },
                            { value: 20, label: '20' },
                        ]}
                        valueLabelDisplay="auto"
                        size="small"
                    />
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Font
                    </Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            value={fontFamily}
                            onChange={(e) => setFontFamily(e.target.value)}
                            sx={{ fontFamily }}
                        >
                            {FONT_OPTIONS.map(({ value, label }) => (
                                <MenuItem key={value} value={value} sx={{ fontFamily: value }}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>
        </Drawer>
    );
};

export { AppearanceDrawer };